import { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { NotificationContext } from '@mdc/ui';
import { useTranslation } from 'react-i18next';
import { CDNService, SERVICE_ERRORS, QueryParams, logService, InternalNavigation } from '@mdc/services';
import OutbreakReport from '@mdc/ui/src/outbreak/OutbreakReport';
import { NOTIFICATION_TYPES } from '@mdc/constants';

const Outbreak = (props) => {
    const { faqs } = props;
    const { date } = QueryParams();
    const [availableDates, setAvailableDates] = useState([]);
    const [detections, setDetections] = useState([]);
    const [hashes, setHashes] = useState([]);
    const [latestDate, setLatestDate] = useState('');
    const [latestReport, setLatestReport] = useState(null);
    const { notify } = useContext(NotificationContext);
    const mainFolder = 'reports/';

    const { t, ready } = useTranslation();

    const trimString = (currentDate) => {
        if (currentDate && currentDate.includes('outbreak-report/')) {
            return currentDate.replace(/outbreak-report\//, '');
        }

        return currentDate;
    };

    const onCDNerror = (data) => {
        logService.log(data); // eslint-disable-line no-console
        notify({
            message: t('Report not available. Viewing the latest report'),
            type: NOTIFICATION_TYPES.CRITICAL
        });
    };

    const onCDNinvalid = () => {
        notify({
            message: t('There was an issue loading the report. Please try again later'),
            type: NOTIFICATION_TYPES.CRITICAL
        });
    };

    const handleCDNnavigate = (path) => {
        InternalNavigation.navigation(path);
    };

    useEffect(() => {
        (async () => {
            try {
                CDNService.on(SERVICE_ERRORS.REPORT_ERROR, onCDNerror);
                CDNService.on(SERVICE_ERRORS.INVALID_REPORT, onCDNinvalid);
                const oldReports = await CDNService.getCDNReport('outbreak-report/older-reports.json', mainFolder, handleCDNnavigate);
                const latest = await CDNService.getCDNReport('outbreak-report-latest.json', mainFolder, handleCDNnavigate);

                // eslint-disable-next-line camelcase
                if (latestDate !== latest?.report_date) {
                    // eslint-disable-next-line camelcase
                    setLatestDate(latest?.report_date);
                }

                if (Array.isArray(oldReports) && latest && hashes) {
                    const oldReportsTemp = oldReports.map((report) => `outbreak-report/${report}`);
                    setAvailableDates(oldReportsTemp);
                    setLatestReport(latest.detections);

                    if (date?.toLowerCase() !== 'latest') {
                        updateDate(date);
                    } else {
                        setDetections(Object.entries(latest.detections));
                    }

                    setHashes(latest.hashes);
                }
            } catch (err)  {
                logService.log(err); // eslint-disable-line no-console
            }
        })();
    }, []);

    useEffect(() => {
        if (date?.toLowerCase() === 'latest') {
            if (latestReport) {
                setDetections(Object.entries(latestReport));
            } else {
                updateDate('Latest');
            }
        }
    }, [date]);

    const processFileName = (fileName) => {
        if (fileName?.toLowerCase() === 'latest') {
            if (latestReport) {
                setDetections(Object.entries(latestReport));
                return;
            }

            fileName = 'outbreak-report-latest';

        } else {
            fileName = `outbreak-report/${fileName}`;
        }

        return fileName;
    };

    const updateDate = (fileName) => {
        if (!fileName) {
            return;
        }

        const dateParam = trimString(fileName);
        window && window.history.replaceState('', '', `?date=${dateParam}`);

        fileName = processFileName(fileName);

        if (fileName) {
            (async() => {
                try {
                    const updatedLatest = await CDNService.getCDNReport(`${fileName}.json`, mainFolder, handleCDNnavigate);

                    if (updatedLatest && updatedLatest.detections) {
                        const identifications = Object.entries(updatedLatest.detections);
                        setDetections(identifications);
                    }

                } catch (error) {
                    logService.error(error); // eslint-disable-line no-console
                }
            })();
        }
    };

    if (!ready) {
        return null;
    }

    return (
        <OutbreakReport
            faqs={faqs(t)}
            availableDates={availableDates}
            hashes={hashes}
            detections={detections}
            updateDate={updateDate}
            latestDate={latestDate}
        />
    );
};

Outbreak.propTypes = {
    faqs: PropTypes.func
};

Outbreak.defaultProps = {
    faqs: []
};

export default Outbreak;
